import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Copy, Globe, Plus, Send, Trash2, Webhook, X } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { API, BACKEND } from "@/lib/api";


const StatusPill = ({ status }) => {
    if (status == null)
        return (
            <span className="rounded border border-slate-700 px-1.5 py-px text-[9px] text-slate-500">
                pending
            </span>
        );
    const ok = status < 400;
    return (
        <span
            className={`rounded border px-1.5 py-px text-[9px] ${
                ok
                    ? "border-emerald-500/30 bg-emerald-950/40 text-emerald-300"
                    : "border-rose-500/30 bg-rose-950/40 text-rose-300"
            }`}
        >
            {status === 599 ? "unreachable" : status}
        </span>
    );
};

export default function OutboundPanel({ appId, deliveries: wsDeliveries, clearStamp = 0, eventTypes = [] }) {
    const [endpoints, setEndpoints] = useState([]);
    const [deliveries, setDeliveries] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [url, setUrl] = useState("");
    const [epName, setEpName] = useState("");
    const [selected, setSelected] = useState([]);
    const [customPat, setCustomPat] = useState("");
    const [secret, setSecret] = useState(null);
    const [copied, setCopied] = useState(false);
    const [confirmingClear, setConfirmingClear] = useState(false);
    const [clearing, setClearing] = useState(false);

    // a clear broadcast (this tab or another) empties the list
    useEffect(() => {
        if (clearStamp) setDeliveries([]);
    }, [clearStamp]);

    const clearDeliveries = async () => {
        setClearing(true);
        try {
            const r = await fetch(`${API}/apps/${appId}/deliveries`, {
                method: "DELETE",
                credentials: "include",
            });
            if (!r.ok) throw new Error("failed");
            const d = await r.json();
            setDeliveries([]);
            toast.success(`Cleared — ${d.deleted} deliver${d.deleted === 1 ? "y" : "ies"} wiped`);
        } catch {
            toast.error("Could not clear deliveries");
        } finally {
            setClearing(false);
            setConfirmingClear(false);
        }
    };
    const [pubType, setPubType] = useState("payment.succeeded");
    const [pubData, setPubData] = useState('{\n  "id": "pay_4f92ka",\n  "amount": 4200\n}');
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const [er, dr] = await Promise.all([
                fetch(`${API}/apps/${appId}/endpoints`, { credentials: "include" }),
                fetch(`${API}/apps/${appId}/deliveries`, { credentials: "include" }),
            ]);
            if (er.ok) setEndpoints((await er.json()).endpoints || []);
            if (dr.ok) setDeliveries((await dr.json()).deliveries || []);
        } catch {
            /* best-effort */
        }
    };

    useEffect(() => {
        let dead = false;
        (async () => {
            if (!dead) await load();
        })();
        return () => {
            dead = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    useEffect(() => {
        if (!wsDeliveries?.length) return;
        setDeliveries((prev) => {
            const seen = new Map();
            [...wsDeliveries, ...prev].forEach((d) => {
                if (!seen.has(d.id)) seen.set(d.id, d);
            });
            return [...seen.values()].slice(0, 60);
        });
    }, [wsDeliveries]);

    const toggleChip = (t) =>
        setSelected((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

    const addCustomPattern = () => {
        const p = customPat.trim();
        if (!p) return;
        if (!selected.includes(p)) setSelected((prev) => [...prev, p]);
        setCustomPat("");
    };

    const register = async () => {
        setBusy(true);
        try {
            const r = await fetch(`${API}/apps/${appId}/endpoints`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    url: url.trim(),
                    name: epName.trim(),
                    events: selected,
                }),
            });
            if (!r.ok) throw new Error("failed");
            const d = await r.json();
            setSecret(d.secret);
            setUrl("");
            setEpName("");
            setSelected([]);
            setCustomPat("");
            setModalOpen(false);
            await load();
            toast.success("Endpoint registered");
        } catch {
            toast.error("Could not register endpoint");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (epId) => {
        try {
            await fetch(`${API}/apps/${appId}/endpoints/${epId}`, {
                method: "DELETE",
                credentials: "include",
            });
            await load();
            toast.success("Endpoint removed");
        } catch {
            toast.error("Remove failed");
        }
    };

    const publish = async () => {
        setBusy(true);
        try {
            let data = {};
            try {
                data = JSON.parse(pubData || "{}");
            } catch {
                toast.error("Payload is not valid JSON");
                setBusy(false);
                return;
            }
            const r = await fetch(`${API}/apps/${appId}/publish`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ type: pubType.trim(), data }),
            });
            if (!r.ok) throw new Error("failed");
            const d = await r.json();
            toast.success("Event published", {
                description: `Matched ${d.matched} endpoint${d.matched === 1 ? "" : "s"}.`,
            });
            await load();
        } catch {
            toast.error("Publish failed");
        } finally {
            setBusy(false);
        }
    };

    const copySecret = async () => {
        try {
            await navigator.clipboard.writeText(secret);
            setCopied(true);
            toast.success("Signing secret copied");
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Copy failed");
        }
    };

    return (
        <div data-testid="outbound-panel" className="glass-panel rounded-xl p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 font-display text-sm font-bold tracking-tight text-slate-100">
                    <Webhook className="h-4 w-4 text-cyan-400" />
                    Outbound webhooks
                </h3>
                <Link
                    to="/settings?tab=portal"
                    data-testid="outbound-portal-link"
                    className="flex items-center gap-1.5 font-mono text-[10px] text-slate-500 hover:text-cyan-300"
                >
                    <Globe className="h-3 w-3" /> Customer portal
                </Link>
            </div>

            {secret && (
                <div
                    data-testid="endpoint-secret-banner"
                    className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-teal-500/30 bg-teal-950/30 px-4 py-3"
                >
                    <div className="min-w-0">
                        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-teal-400">
                            Signing secret — shown once
                        </div>
                        <code className="mt-1 block truncate font-mono text-[11px] text-teal-200">
                            {secret}
                        </code>
                    </div>
                    <div className="flex gap-2">
                        <button
                            data-testid="endpoint-secret-copy"
                            onClick={copySecret}
                            className="flex items-center gap-1.5 rounded border border-slate-700 px-2.5 py-1.5 font-mono text-[10px] text-slate-300 hover:border-teal-500/40"
                        >
                            {copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                            {copied ? "copied" : "copy"}
                        </button>
                        <button
                            data-testid="endpoint-secret-dismiss"
                            onClick={() => setSecret(null)}
                            className="rounded border border-slate-700 px-2.5 py-1.5 font-mono text-[10px] text-slate-500 hover:text-slate-300"
                        >
                            dismiss
                        </button>
                    </div>
                </div>
            )}

            <div className="mt-4 flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    {endpoints.length} destination{endpoints.length === 1 ? "" : "s"}
                </span>
                <button
                    data-testid="endpoint-add-button"
                    onClick={() => setModalOpen(true)}
                    className="flex items-center gap-1.5 rounded-md bg-cyan-400 px-4 py-2 font-mono text-[11px] font-bold text-slate-950 shadow-[0_0_18px_rgba(6,182,212,0.35)] transition-all hover:bg-cyan-300"
                >
                    <Plus className="h-3.5 w-3.5" /> Add endpoint
                </button>
            </div>

            <div className="mt-3 space-y-2">
                {endpoints.length === 0 && (
                    <div
                        data-testid="endpoints-empty"
                        className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-slate-800 py-10 text-center"
                    >
                        <Webhook className="h-6 w-6 text-slate-700" />
                        <div>
                            <p className="font-mono text-xs font-semibold text-slate-400">No endpoints yet</p>
                            <p className="mt-1 font-mono text-[11px] text-slate-600">
                                Create your first destination and published events start flowing to it, signed and retried.
                            </p>
                        </div>
                        <button
                            data-testid="endpoints-empty-cta"
                            onClick={() => setModalOpen(true)}
                            className="flex items-center gap-1.5 rounded-md border border-cyan-500/40 bg-cyan-950/40 px-4 py-2 font-mono text-[11px] font-semibold text-cyan-300 transition-colors hover:bg-cyan-950/70"
                        >
                            <Plus className="h-3 w-3" /> Create first endpoint
                        </button>
                    </div>
                )}
                {endpoints.map((ep) => (
                    <div
                        key={ep.ep_id}
                        data-testid={`endpoint-row-${ep.ep_id}`}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-800/80 bg-slate-950/50 px-4 py-2.5 font-mono text-[11px]"
                    >
                        <div className="flex min-w-0 flex-wrap items-center gap-2">
                            {ep.name && (
                                <span className="font-semibold text-slate-200">{ep.name}</span>
                            )}
                            <span className="truncate text-cyan-300">{ep.url}</span>
                            {(ep.events || []).length === 0 ? (
                                <span className="rounded-full border border-slate-700 px-2 py-px text-[9px] text-slate-500">
                                    all events
                                </span>
                            ) : (
                                (ep.events || []).map((e) => (
                                    <span
                                        key={e}
                                        className="rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2 py-px text-[9px] text-emerald-300"
                                    >
                                        {e}
                                    </span>
                                ))
                            )}
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-slate-500">
                                {ep.health?.success_rate == null
                                    ? "no deliveries"
                                    : `${ep.health.success_rate}% ok`}
                            </span>
                            <button
                                data-testid={`endpoint-delete-${ep.ep_id}`}
                                onClick={() => remove(ep.ep_id)}
                                className="flex items-center gap-1 text-slate-500 transition-colors hover:text-rose-300"
                            >
                                <Trash2 className="h-3 w-3" /> remove
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-5 rounded-lg border border-slate-800/80 bg-slate-950/50 p-4">
                <div className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    <Send className="h-3 w-3" /> Publish a test event
                </div>
                <div className="grid gap-2 sm:grid-cols-[220px_1fr_auto]">
                    <input
                        data-testid="publish-type-input"
                        value={pubType}
                        onChange={(e) => setPubType(e.target.value)}
                        placeholder="event.type"
                        className="rounded-md border border-slate-800 bg-slate-950/70 px-3.5 py-2 font-mono text-[11px] text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none"
                    />
                    <textarea
                        data-testid="publish-data-input"
                        value={pubData}
                        onChange={(e) => setPubData(e.target.value)}
                        rows={2}
                        className="rounded-md border border-slate-800 bg-slate-950/70 px-3.5 py-2 font-mono text-[11px] text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none"
                    />
                    <button
                        data-testid="publish-event-button"
                        onClick={publish}
                        disabled={busy || !pubType.trim()}
                        className="self-start rounded-md border border-cyan-500/40 bg-cyan-950/40 px-4 py-2 font-mono text-[11px] font-semibold text-cyan-300 transition-colors hover:bg-cyan-950/70 disabled:opacity-50"
                    >
                        Publish
                    </button>
                </div>
            </div>

            <div className="mt-4 space-y-1.5">
                <div className="flex items-center justify-between">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                        Recent deliveries
                    </div>
                    {!confirmingClear ? (
                        <button
                            data-testid="deliveries-clear-button"
                            onClick={() => setConfirmingClear(true)}
                            disabled={deliveries.length === 0}
                            className="flex items-center gap-1 rounded border border-slate-700/80 px-2 py-1 font-mono text-[10px] text-slate-400 transition-colors hover:border-rose-500/50 hover:text-rose-300 disabled:opacity-40"
                        >
                            <Trash2 className="h-3 w-3" /> clear
                        </button>
                    ) : (
                        <button
                            data-testid="deliveries-clear-confirm-button"
                            onClick={clearDeliveries}
                            onBlur={() => setConfirmingClear(false)}
                            disabled={clearing}
                            autoFocus
                            className="rounded border border-rose-500/50 bg-rose-950/40 px-2.5 py-1 font-mono text-[10px] font-semibold text-rose-300 transition-colors hover:bg-rose-950/70 disabled:opacity-50"
                        >
                            {clearing ? "wiping…" : `wipe ${deliveries.length}?`}
                        </button>
                    )}
                </div>
                {deliveries.length === 0 && (
                    <p className="rounded-lg border border-dashed border-slate-800 py-6 text-center font-mono text-[11px] text-slate-600">
                        Nothing delivered yet — publish an event to see it flow.
                    </p>
                )}
                {deliveries.map((d) => (
                    <div
                        key={d.id}
                        data-testid={`delivery-row-${d.id}`}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-800/80 bg-slate-950/50 px-4 py-2.5 font-mono text-[11px]"
                    >
                        <div className="flex min-w-0 items-center gap-2.5">
                            <span className="text-slate-600">
                                {(d.created_at || "").slice(11, 19)}
                            </span>
                            <span className="text-slate-300">{d.event_type}</span>
                            <span className="hidden max-w-[240px] truncate text-slate-500 sm:inline">
                                → {d.endpoint_url}
                            </span>
                        </div>
                        <div className="flex items-center gap-3">
                            {d.attempts > 1 && (
                                <span className="text-amber-300">attempt {d.attempts}</span>
                            )}
                            {d.latency != null && (
                                <span className="text-slate-600">{d.latency}ms</span>
                            )}
                            <StatusPill status={d.status} />
                        </div>
                    </div>
                ))}
            </div>

            <AnimatePresence>
                {modalOpen && (
                    <motion.div
                        data-testid="endpoint-modal"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.18 }}
                        className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
                        onClick={() => setModalOpen(false)}
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 14, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.98 }}
                            transition={{ duration: 0.22, ease: "easeOut" }}
                            onClick={(e) => e.stopPropagation()}
                            className="glass-panel w-full max-w-md rounded-xl p-6 shadow-2xl"
                        >
                            <div className="flex items-start justify-between">
                                <div>
                                    <h4 className="flex items-center gap-2 font-display text-base font-bold tracking-tight text-slate-100">
                                        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-cyan-500/20">
                                            <Webhook className="h-3.5 w-3.5 text-cyan-300" />
                                        </span>
                                        Create a new webhook
                                    </h4>
                                    <p className="mt-1.5 font-mono text-[11px] leading-relaxed text-slate-500">
                                        Events published to this app get signed and delivered to your endpoint.
                                    </p>
                                </div>
                                <button
                                    data-testid="endpoint-modal-close"
                                    onClick={() => setModalOpen(false)}
                                    className="rounded-md p-1 text-slate-500 transition-colors hover:bg-slate-800 hover:text-slate-300"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>

                            <div className="mt-5 space-y-4">
                                <div>
                                    <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                                        Webhook name
                                    </label>
                                    <input
                                        data-testid="endpoint-name-input"
                                        value={epName}
                                        onChange={(e) => setEpName(e.target.value)}
                                        placeholder="e.g. Acme production"
                                        className="w-full rounded-md border border-slate-800 bg-slate-950/70 px-3.5 py-2 font-mono text-[11px] text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none focus:ring-1 focus:ring-cyan-500/40"
                                    />
                                </div>
                                <div>
                                    <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                                        Endpoint URL
                                    </label>
                                    <input
                                        data-testid="endpoint-url-input"
                                        value={url}
                                        onChange={(e) => setUrl(e.target.value)}
                                        placeholder="https://customer.app/hooks/mdh"
                                        className="w-full rounded-md border border-slate-800 bg-slate-950/70 px-3.5 py-2 font-mono text-[11px] text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none focus:ring-1 focus:ring-cyan-500/40"
                                    />
                                </div>
                                <div>
                                    <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                                        Trigger events
                                    </label>
                                    <div className="flex flex-wrap gap-1.5">
                                        <button
                                            data-testid="endpoint-event-chip-all"
                                            onClick={() => setSelected([])}
                                            className={`rounded-full border px-2.5 py-1 font-mono text-[10px] transition-colors ${
                                                selected.length === 0
                                                    ? "border-cyan-400/60 bg-cyan-500/20 text-cyan-200"
                                                    : "border-slate-700 text-slate-500 hover:border-slate-600 hover:text-slate-300"
                                            }`}
                                        >
                                            All events
                                        </button>
                                        {eventTypes.map((t) => (
                                            <button
                                                key={t}
                                                data-testid={`endpoint-event-chip-${t}`}
                                                onClick={() => toggleChip(t)}
                                                className={`rounded-full border px-2.5 py-1 font-mono text-[10px] transition-colors ${
                                                    selected.includes(t)
                                                        ? "border-emerald-400/50 bg-emerald-500/15 text-emerald-300"
                                                        : "border-slate-700 text-slate-500 hover:border-slate-600 hover:text-slate-300"
                                                }`}
                                            >
                                                {t}
                                            </button>
                                        ))}
                                        {selected
                                            .filter((s) => !eventTypes.includes(s))
                                            .map((s) => (
                                                <button
                                                    key={s}
                                                    data-testid={`endpoint-event-chip-custom-${s}`}
                                                    onClick={() => toggleChip(s)}
                                                    title="custom pattern — click to remove"
                                                    className="flex items-center gap-1 rounded-full border border-emerald-400/50 bg-emerald-500/15 px-2.5 py-1 font-mono text-[10px] text-emerald-300 transition-colors hover:border-rose-500/40 hover:text-rose-300"
                                                >
                                                    {s}
                                                    <X className="h-2.5 w-2.5" />
                                                </button>
                                            ))}
                                    </div>
                                    <div className="mt-2 flex gap-2">
                                        <input
                                            data-testid="endpoint-custom-pattern-input"
                                            value={customPat}
                                            onChange={(e) => setCustomPat(e.target.value)}
                                            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomPattern())}
                                            placeholder="custom pattern — e.g. payment.*"
                                            className="min-w-0 flex-1 rounded-md border border-slate-800 bg-slate-950/70 px-3 py-1.5 font-mono text-[10px] text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none"
                                        />
                                        <button
                                            data-testid="endpoint-custom-pattern-add"
                                            onClick={addCustomPattern}
                                            disabled={!customPat.trim()}
                                            className="shrink-0 rounded-md border border-slate-700 px-2.5 py-1.5 font-mono text-[10px] text-slate-400 transition-colors hover:border-cyan-500/40 hover:text-cyan-300 disabled:opacity-40"
                                        >
                                            + add
                                        </button>
                                    </div>
                                    {selected.length > 0 && (
                                        <p className="mt-2 font-mono text-[10px] text-slate-600">
                                            receives: {selected.join(", ")}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="mt-6 flex items-center justify-end gap-2">
                                <button
                                    data-testid="endpoint-modal-cancel"
                                    onClick={() => setModalOpen(false)}
                                    className="rounded-md px-4 py-2 font-mono text-[11px] text-slate-500 transition-colors hover:text-slate-300"
                                >
                                    Cancel
                                </button>
                                <button
                                    data-testid="endpoint-register-button"
                                    onClick={register}
                                    disabled={busy || url.trim().length < 8}
                                    className="rounded-md bg-cyan-400 px-5 py-2 font-mono text-[11px] font-bold text-slate-950 shadow-[0_0_18px_rgba(6,182,212,0.35)] transition-all hover:bg-cyan-300 disabled:opacity-50"
                                >
                                    {busy ? "Creating…" : "Create webhook"}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
